import { Info } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import {
  SELECTABLE_OUTPUT_FORMAT_OPTIONS,
  OutputFormat,
  OutputFormatOption,
} from '@/constants/outputFormat';

interface OutputFormatSelectorProps {
  value: OutputFormat;
  onChange: (value: OutputFormat) => void;
}

/** Mini-tableau monospace affiché dans la hover-card (exemple de sortie). */
const OutputFormatPreview = ({ preview }: { preview: OutputFormatOption['preview'] }) => (
  <table className="border-collapse text-[10px] font-mono leading-tight" aria-hidden="true">
    <thead>
      <tr>
        {preview.headers.map((header) => (
          <th
            key={header}
            className="border border-border bg-muted px-1.5 py-0.5 text-left font-medium text-muted-foreground"
          >
            {header}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {preview.rows.map((row, rowIndex) => (
        <tr key={rowIndex}>
          {row.map((cell, cellIndex) => (
            <td
              key={cellIndex}
              className="border border-border px-1.5 py-0.5 text-foreground"
            >
              {cell}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

export const OutputFormatSelector = ({ value, onChange }: OutputFormatSelectorProps) => {
  return (
    <fieldset>
      <legend className="text-sm font-medium">Format du fichier de sortie</legend>
      <RadioGroup
        value={value}
        onValueChange={(next) => onChange(next as OutputFormat)}
        className="mt-1.5 gap-1.5"
        aria-label="Format du fichier de sortie"
      >
        {SELECTABLE_OUTPUT_FORMAT_OPTIONS.map((option) => {
          const inputId = `output-format-${option.value}`;
          const isSelected = value === option.value;
          return (
            <div
              key={option.value}
              className={cn(
                'flex items-center gap-2 rounded-lg border p-2 transition-colors',
                isSelected ? 'border-primary bg-primary/5' : 'border-border'
              )}
            >
              <RadioGroupItem id={inputId} value={option.value} />
              <Label htmlFor={inputId} className="flex-1 cursor-pointer text-sm font-medium">
                {option.label}
              </Label>
              {/* Description + table d'exemple compactées au survol (hover-card). */}
              <HoverCard openDelay={100} closeDelay={50}>
                <HoverCardTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    aria-label={`Détails du format « ${option.label} »`}
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </HoverCardTrigger>
                <HoverCardContent align="end" className="w-auto max-w-xs space-y-2">
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                  <OutputFormatPreview preview={option.preview} />
                </HoverCardContent>
              </HoverCard>
            </div>
          );
        })}
      </RadioGroup>
    </fieldset>
  );
};
